import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { Button, Dropdown, Icon } from '~/components';
import { colors } from '~/styles';

const EXPANDED_WIDTH = 257;
const COLLAPSED_WIDTH = 48;
const HEADER_HEIGHT = '3.75rem';

const S = {
  Wrapper: styled.div`
    flex-shrink: 0;
    width: ${({ $expanded, $overlay }) => ($overlay ? COLLAPSED_WIDTH : $expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH)}px;
    background: ${colors.grey5};
    display: flex;
    flex-direction: column;
    transition: width 200ms ease;
    border-left: 1px solid ${colors.grey10};
    height: 100%;
    overflow: ${({ $overlay }) => ($overlay ? 'visible' : 'hidden')};
    position: relative;
  `,

  OverlayScrim: styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    background: transparent;
    z-index: 1;
  `,

  OverlayContent: styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: ${({ $expanded }) => ($expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH)}px;
    background: ${colors.grey5};
    display: flex;
    flex-direction: column;
    overflow: hidden;
    z-index: 2;
    transition: width 200ms ease;
    border-left: 1px solid ${colors.grey10};
    box-shadow: ${({ $expanded }) => ($expanded ? '-4px 0 12px rgba(0, 0, 0, 0.1)' : 'none')};
  `,

  ExpandedHeader: styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    height: ${HEADER_HEIGHT};
    padding: 0 0.75rem;
    flex-shrink: 0;
    font-weight: 600;
    font-size: 0.9375rem;
    color: ${colors.black};
    white-space: nowrap;
  `,

  CollapseButton: styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    min-width: 1.75rem;
    padding: 0;
    border: none;
    border-radius: 0;
    background: transparent;
    color: ${colors.grey40};
    cursor: pointer;
    font-size: 0.75rem;
    margin-left: auto;
    flex-shrink: 0;

    &:hover {
      background: ${colors.grey10};
      color: ${colors.primary};
    }
  `,

  CollapsedHeader: styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: ${HEADER_HEIGHT};
    flex-shrink: 0;
  `,

  CollapsedToggle: styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    min-width: 1.75rem;
    border: none;
    border-radius: 0.25rem;
    background: transparent;
    color: ${colors.primary};
    cursor: pointer;
    font-size: 0.875rem;
    padding: 0;

    .expand-icon {
      display: none;
      font-size: 0.75rem;
      color: ${colors.grey40};
    }

    &:hover {
      background: ${colors.grey10};

      .sparkles-icon {
        display: none;
      }
      .expand-icon {
        display: block;
        color: ${colors.primary};
      }
    }
  `,

  CollapsedBody: styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
  `,

  CollapsedNewChatButton: styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    border: none;
    border-radius: 0.25rem;
    background: transparent;
    color: ${colors.grey40};
    cursor: pointer;
    font-size: 0.75rem;
    flex-shrink: 0;
    padding: 0;

    &:hover {
      background: ${colors.grey10};
      color: ${colors.primary};
    }
  `,

  ExpandedBody: styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-width: 0;
  `,

  NewChatButton: styled.div`
    padding: 0 0.75rem 0.75rem;
    flex-shrink: 0;

    .button {
      width: 100%;
      font-size: 0.8125rem;
    }
  `,

  SectionHeader: styled.div`
    display: flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    cursor: pointer;
    user-select: none;

    &:hover {
      color: ${colors.grey75};
    }
  `,

  SectionTitle: styled.span`
    font-size: 0.6875rem;
    font-weight: 600;
    color: ${colors.grey40};
    text-transform: uppercase;
    letter-spacing: 0.03em;
    flex: 1;
  `,

  SectionChevron: styled.div`
    display: flex;
    align-items: center;
    font-size: 0.5rem;
    color: ${colors.grey40};
    transition: transform 150ms ease;
    transform: rotate(${({ $open }) => ($open ? '0deg' : '-90deg')});
  `,

  ConversationList: styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 0.25rem 0.5rem;

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: ${colors.grey25};
      border-radius: 2px;
    }
  `,

  ConversationItem: styled.div`
    display: flex;
    align-items: center;
    padding: 0.25rem 0.5rem;
    border-radius: 0.375rem;
    cursor: pointer;
    margin-bottom: 1px;
    position: relative;

    ${({ $active }) =>
      $active
        ? css`
            background: ${colors.grey10};
            font-weight: 600;
          `
        : css`
            &:hover {
              background: ${colors.grey10};
            }
          `}

    &:hover .conversation-actions {
      opacity: 1;
    }
  `,

  ConversationTitle: styled.span`
    font-size: 0.8125rem;
    color: ${colors.grey75};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
  `,

  ConversationActions: styled.div`
    opacity: 0;
    flex-shrink: 0;
    margin-left: 0.25rem;
  `,

  ActionButton: styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    min-width: 1.5rem;
    padding: 0;
    border: none;
    border-radius: 0.25rem;
    background: transparent;
    color: ${colors.grey40};
    cursor: pointer;
    font-size: 0.625rem;

    &:hover {
      background: ${colors.grey20};
      color: ${colors.grey40};
    }
  `,

  ConversationDropdown: styled(Dropdown)`
    .conversation-menu-item {
      font-size: 0.8125rem;

      .icon {
        font-size: 0.75rem;
      }
    }
  `,

  RenameInput: styled.input`
    font-size: 0.8125rem;
    color: ${colors.grey75};
    border: 1px solid ${colors.primary};
    border-radius: 0.25rem;
    padding: 0.125rem 0.25rem;
    outline: none;
    width: 100%;
    background: ${colors.white};
  `,
};

function ConversationItemRow({ conversation, isActive, onSelect, onRename, onDelete }) {
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(conversation.title);

  const handleRenameSubmit = () => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== conversation.title) {
      onRename(conversation.id, trimmed);
    }
    setIsRenaming(false);
  };

  if (isRenaming) {
    return (
      <S.ConversationItem $active={isActive}>
        <S.RenameInput
          autoFocus
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onBlur={handleRenameSubmit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleRenameSubmit();
            if (e.key === 'Escape') setIsRenaming(false);
          }}
        />
      </S.ConversationItem>
    );
  }

  return (
    <S.ConversationItem $active={isActive} onClick={() => onSelect(conversation.id)}>
      <S.ConversationTitle>{conversation.title}</S.ConversationTitle>
      <S.ConversationActions className="conversation-actions">
        <S.ConversationDropdown align="right">
          {({ setIsOpen: setDropdownOpen }) => (
            <>
              <Dropdown.Trigger>
                <S.ActionButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setDropdownOpen(true);
                  }}>
                  <Icon icon="ellipsis-h" />
                </S.ActionButton>
              </Dropdown.Trigger>
              <Dropdown.Menu className="conversation-menu">
                <Dropdown.Item
                  className="conversation-menu-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsRenaming(true);
                    setRenameValue(conversation.title);
                    setDropdownOpen(false);
                  }}>
                  Rename
                </Dropdown.Item>
                <Dropdown.Item
                  className="conversation-menu-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(conversation.id);
                    setDropdownOpen(false);
                  }}>
                  Delete
                </Dropdown.Item>
              </Dropdown.Menu>
            </>
          )}
        </S.ConversationDropdown>
      </S.ConversationActions>
    </S.ConversationItem>
  );
}

export default function AssistantSidebar({
  mode,
  conversations,
  activeConversationId,
  onSelectConversation,
  onNewChat,
  onRenameConversation,
  onDeleteConversation,
  sidebarOpen,
  onToggleSidebar,
}) {
  const [historyOpen, setHistoryOpen] = useState(true);

  const isPanel = mode === 'panel';

  const sidebarContent = sidebarOpen ? (
    <>
      <S.ExpandedHeader>
        <Icon icon="sparkles" color={colors.primary} style={{ fontSize: '0.875rem' }} />
        Ask Ruddr
        <S.CollapseButton onClick={onToggleSidebar} title="Collapse sidebar">
          <Icon icon="table-columns" />
        </S.CollapseButton>
      </S.ExpandedHeader>
      <S.ExpandedBody>
        <S.NewChatButton>
          <Button isOutline onClick={onNewChat}>
            <Icon icon="plus" spaceRight />
            Start New Chat
          </Button>
        </S.NewChatButton>

        <S.SectionHeader onClick={() => setHistoryOpen((prev) => !prev)}>
          <S.SectionTitle>Chat History</S.SectionTitle>
          <S.SectionChevron $open={historyOpen}>
            <Icon icon="chevron-down" />
          </S.SectionChevron>
        </S.SectionHeader>

        {historyOpen && (
          <S.ConversationList>
            {conversations.map((c) => (
              <ConversationItemRow
                key={c.id}
                conversation={c}
                isActive={c.id === activeConversationId}
                onSelect={onSelectConversation}
                onRename={onRenameConversation}
                onDelete={onDeleteConversation}
              />
            ))}
          </S.ConversationList>
        )}
      </S.ExpandedBody>
    </>
  ) : (
    <>
      <S.CollapsedHeader>
        <S.CollapsedToggle onClick={onToggleSidebar} title="Expand sidebar">
          <Icon icon="sparkles" className="sparkles-icon" />
          <Icon icon="table-columns" className="expand-icon" />
        </S.CollapsedToggle>
      </S.CollapsedHeader>
      <S.CollapsedBody>
        <S.CollapsedNewChatButton onClick={onNewChat} title="Start New Chat">
          <Icon icon="plus" />
        </S.CollapsedNewChatButton>
      </S.CollapsedBody>
    </>
  );

  return (
    <S.Wrapper $expanded={sidebarOpen} $overlay={isPanel}>
      {isPanel ? (
        <>
          {sidebarOpen && <S.OverlayScrim onClick={onToggleSidebar} />}
          <S.OverlayContent $expanded={sidebarOpen}>{sidebarContent}</S.OverlayContent>
        </>
      ) : (
        sidebarContent
      )}
    </S.Wrapper>
  );
}
